import { Field, useField, useFormikContext } from "formik";
import React, { useEffect } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { useStrings } from "../../localization/localizationHelpers";
import { EnableInputOptions } from "../../models/pitchUxModels";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "../CustomCheckBoxField/CustomCheckBoxField.module.css";
import { EmbeddedOtherTextField } from "../EmbeddedOtherTextField/EmbeddedOtherTextField";
import { FieldErrorMessage } from "../FieldErrorMessage";
import { FieldHeader, FieldHeaderProps, FieldStateProps } from "../FieldHeader";
import { useSetFieldTouched } from "../useSetFieldTouched";

export type CheckBox = Readonly<{
  value: string;
  label: string;
  subtitle?: string;
}>;
export enum DisplayOptions {
  row = "ROW",
  column = "COLUMN",
}
export type CustomCheckBoxFieldProps = Readonly<{
  checkBoxes: CheckBox[];
  display?: DisplayOptions;
  parentChecked?: boolean;
  isOtherChecked?: boolean;
  name: string;
  setChildInputForm?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}> &
  FieldHeaderProps &
  FieldStateProps;

export const CustomCheckBoxField = (props: CustomCheckBoxFieldProps) => {
  const [other] = useStrings("other");
  const {
    name,
    checkBoxes,
    display,
    isSaving,
    disabled,
    parentChecked,
    isOtherChecked,
    setChildInputForm,
  } = props;
  const [field] = useField(props);
  useSetFieldTouched(field.name);

  const { setFieldValue } = useFormikContext();

  const matchChildInputNameValuesMap = new Map([
    ["genre", "genreOther"],
    ["releasedPlatforms", "releasedPlatformsOther"],
    ["programConsiderationType", "programConsiderationTypeOther"],
  ]);

  const embeddedInputName = matchChildInputNameValuesMap.get(field.name);

  const [otherInputField] = useField({
    name: embeddedInputName ?? "",
  });

  useEffect(() => {
    if (
      !field.value.includes("Other") &&
      field.value.length > 0 &&
      embeddedInputName &&
      otherInputField.value !== ""
    ) {
      setFieldValue(embeddedInputName, "");
    }
  }, [embeddedInputName, otherInputField.value, field.value, setFieldValue]);

  const displayMapOptions = new Map([
    [DisplayOptions.row, styles.displayOptionsAsRow],
    [DisplayOptions.column, undefined],
  ]);

  useEffect(() => {
    if (parentChecked === false && field.value.length > 0) {
      setFieldValue(field.name, []);
    }
  }, [field, field.name, field.value, parentChecked, setFieldValue]);

  const check = (value: string[]) => {
    if (setChildInputForm) {
      if (value.includes(EnableInputOptions.Other)) {
        setChildInputForm(true);
      } else {
        setChildInputForm(false);
      }
    }
  };

  return (
    <Row className={commonStyles.container}>
      <Column>
        <FieldHeader {...props} />
        <div role="group" aria-labelledby="checkbox-group">
          <div className={display ? displayMapOptions.get(display) : styles.displayOptionsAsGrid}>
            {checkBoxes.map(checkBox => (
              <Row key={`checkbox-${checkBox.value}`}>
                <Column>
                  <div>
                    <label className={styles.option}>
                      <Field
                        type="checkbox"
                        name={name}
                        value={disabled ? false : checkBox.value}
                        data-testid={name}
                        disabled={isSaving || disabled || parentChecked === false}
                        InputProps={{
                          onchange: setChildInputForm ? check(field.value) : undefined,
                        }}
                      />
                      {checkBox.label}
                      {checkBox.label === other && embeddedInputName && (
                        <EmbeddedOtherTextField
                          name={embeddedInputName}
                          isRequiredField={isOtherChecked}
                          isSaving={isSaving}
                          parentChecked={isOtherChecked}
                        />
                      )}
                    </label>
                  </div>
                  {checkBox.subtitle && <p className={styles.subtitle}>{checkBox.subtitle}</p>}
                </Column>
              </Row>
            ))}
          </div>
        </div>
        <FieldErrorMessage fieldName={field.name} />
      </Column>
    </Row>
  );
};
